/**
 * DeviceSettings — Camera selection, video resolution, and virtual camera routing.
 * Clean, consumer-friendly interface without developer TMI.
 */

import { useState, useEffect } from "react";
import { SettingsGroup, SettingsRow, Toggle } from "./SettingsPrimitives";

export function DeviceSettings() {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string>("");
  const [resolution, setResolution] = useState<"720p" | "1080p">("720p");
  const [mirrorVideo, setMirrorVideo] = useState<boolean>(true);

  useEffect(() => {
    if (navigator.mediaDevices?.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices().then((devs) => {
        const videoInputs = devs.filter((d) => d.kind === "videoinput");
        setDevices(videoInputs);
        if (videoInputs.length > 0 && !selectedDevice) {
          setSelectedDevice(videoInputs[0].deviceId);
        }
      }).catch(() => {});
    }
  }, [selectedDevice]);

  return (
    <div>
      <div style={{ fontSize: "var(--text-lg)", fontWeight: "var(--weight-bold)", color: "var(--text-primary)", marginBottom: "var(--space-6)" }}>
        Device &amp; Video
      </div>

      <SettingsGroup title="Camera Input">
        <SettingsRow label="Video Capture Device" description="Select the local webcam to feed into neural face swap">
          <select
            value={selectedDevice}
            onChange={(e) => setSelectedDevice(e.target.value)}
            style={{
              background: "var(--bg-overlay)",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-md)",
              color: "var(--text-primary)",
              padding: "6px 10px",
              fontSize: "var(--text-xs)",
              outline: "none",
              cursor: "pointer",
              maxWidth: "240px",
            }}
          >
            {devices.map((d, i) => (
              <option key={d.deviceId || i} value={d.deviceId}>
                {d.label || `Camera ${i + 1}`}
              </option>
            ))}
          </select>
        </SettingsRow>

        <SettingsRow label="Mirror Preview" description="Flip the local webcam preview horizontally">
          <Toggle checked={mirrorVideo} onChange={() => setMirrorVideo((v) => !v)} />
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup title="Streaming Quality">
        <SettingsRow label="WebRTC Stream Resolution" description="High-speed JPEG compressed frame pipeline">
          <div style={{ display: "flex", gap: "var(--space-2)" }}>
            <button
              onClick={() => setResolution("720p")}
              style={{
                padding: "4px 12px",
                borderRadius: "var(--radius-md)",
                border: `1px solid ${resolution === "720p" ? "var(--accent)" : "var(--border-subtle)"}`,
                background: resolution === "720p" ? "var(--accent-dim)" : "var(--bg-overlay)",
                color: resolution === "720p" ? "var(--accent)" : "var(--text-muted)",
                fontSize: "var(--text-xs)",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              720p (30 FPS)
            </button>
            <button
              onClick={() => setResolution("1080p")}
              style={{
                padding: "4px 12px",
                borderRadius: "var(--radius-md)",
                border: `1px solid ${resolution === "1080p" ? "var(--accent)" : "var(--border-subtle)"}`,
                background: resolution === "1080p" ? "var(--accent-dim)" : "var(--bg-overlay)",
                color: resolution === "1080p" ? "var(--accent)" : "var(--text-muted)",
                fontSize: "var(--text-xs)",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              1080p (HQ)
            </button>
          </div>
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup title="Virtual Camera Output">
        <div style={{
          padding: "var(--space-4)",
          background: "var(--bg-overlay)",
          borderRadius: "var(--radius-lg)",
          border: "1px solid var(--border-subtle)",
          fontSize: "var(--text-xs)",
          color: "var(--text-secondary)",
          lineHeight: 1.6,
        }}>
          <div style={{ fontWeight: 600, color: "var(--text-primary)", marginBottom: "4px" }}>
            Broadcasting to Zoom, Discord &amp; Google Meet
          </div>
          <div>
            1. Toggle the <strong>Virtual Cam</strong> button in the main Face Swap toolbar.
          </div>
          <div>
            2. In your conferencing app, select <strong>OBS Virtual Camera</strong> or <strong>StreamPersona Virtual Cam</strong> as your video input.
          </div>
        </div>
      </SettingsGroup>
    </div>
  );
}
